import React, { useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';

// Importing SVGs
import btcIcon from '../assets/svg/btc';
import ethIcon from '../assets/svg/eth';
import usdtIcon from '../assets/svg/usdt';
import usdcIcon from '../assets/svg/usdc';
import daiIcon from '../assets/svg/dai';
import busdIcon from '../assets/svg/busd';
import ltcIcon from '../assets/svg/ltc';
import trxIcon from '../assets/svg/trx';
import dogeIcon from '../assets/svg/doge';
import shibIcon from '../assets/svg/shib';
import bnbIcon from '../assets/svg/bnb';
import maticIcon from '../assets/svg/matic';
import cakeIcon from '../assets/svg/cake';
import {AuthContext} from "../AuthContext"; 
import {useContext } from "react"; 

const PaymentForm = ({ updateInstruction }) => {
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('usd');  // This is the base currency (USD)
  const [payCurrency, setPayCurrency] = useState('btc');  // Cryptocurrency for payment
  const [loading, setLoading] = useState(false);
  const [paymentUrl, setPaymentUrl] = useState('');
  const [error, setError] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const {user} = useContext(AuthContext); 
  const apiURL = import.meta.env.VITE_API_URL;
  const emailaddress = localStorage.getItem("emailaddress"); 

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (amount <= 0) {
      setError('Please enter a valid amount.');
      setLoading(false);
      return;
    }

    try {
		console.log("checking for user email address"); 
		console.log(emailaddress); 
      const response = await axios.post(`${apiURL}/api/createPayment`, {
        amount: amount,
        currency: currency,  // Base currency (USD)
        payCurrency: payCurrency,  // Selected cryptocurrency for payment
        order_id: `${emailaddress}`,
      });
      setPaymentUrl(response.data.invoice_url);
    } catch (error) {
      console.error('Payment error:', error);
      setError('Payment failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAmount = (e) => {
    setAmount(e.target.value);
    updateInstruction(e.target.value);
  };
const cryptos = [
  { value: 'btc', label: 'Bitcoin (BTC)', icon: btcIcon },
  { value: 'eth', label: 'Ethereum (ETH)', icon: ethIcon },
  { value: 'usdtbsc', label: 'Tether (USDT)', icon: usdtIcon },
  { value: 'usdcbsc', label: 'USD Coin (USDC)', icon: usdcIcon },
  { value: 'daibsc', label: 'DAI', icon: daiIcon },
  { value: 'busdbsc', label: 'Binance USD (BUSD)', icon: busdIcon },
  { value: 'ltc', label: 'Litecoin (LTC)', icon: ltcIcon },
  { value: 'trx', label: 'Tron (TRX)', icon: trxIcon },
  { value: 'doge', label: 'Dogecoin (DOGE)', icon: dogeIcon },
  { value: 'shibbsc', label: 'Shiba Inu (SHIB)', icon: shibIcon },
  { value: 'bnbbsc', label: 'Binance Coin (BNB)', icon: bnbIcon },
  { value: 'maticpolygon', label: 'Polygon (MATIC)', icon: maticIcon },
  { value: 'cakebsc', label: 'PancakeSwap (CAKE)', icon: cakeIcon }
];


   return (
    <motion.div
      className="p-8 bg-white shadow-md rounded-lg max-w-lg mx-auto"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <h2 className="text-3xl font-bold mb-6 text-center text-blue-500">Deposit Your Account</h2>

      <form onSubmit={handlePaymentSubmit} className="space-y-6">
        {/* Amount Input */}
        <div className="relative">
          <label className="text-sm font-semibold text-gray-700 block mb-1">Amount (USD)</label>
          <motion.input
            whileFocus={{ scale: 1.03, boxShadow: "0px 0px 5px rgba(0,0,0,0.2)" }}
            type="number"
            value={amount}
            onChange={handleAmount}
            className="w-full px-4 py-2 border border-gray-300 rounded-md"
            required
          />
        </div>

        {/* Custom Cryptocurrency Dropdown */}
        <div className="relative">
          <label className="text-sm font-semibold text-gray-700 block mb-1">Choose Cryptocurrency</label>
          <div 
            className="flex items-center justify-between border border-gray-300 rounded-md px-4 py-2 cursor-pointer" 
            onClick={() => setIsOpen(!isOpen)}
          >
            <p className="flex items-center text-gray-800">
              {cryptos.find(crypto => crypto.value === payCurrency)?.icon && (
                <p className="mr-2 w-6 h-6">{React.createElement(cryptos.find(crypto => crypto.value === payCurrency)?.icon)}</p>
              )}
              {cryptos.find(crypto => crypto.value === payCurrency)?.label}
            </p>
            <p className="text-gray-600">&#x25BC;</p>
          </div>
          {isOpen && (
            <div className="relative z-50 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg bg-white">
              {cryptos.map((crypto) => (
                <div
                  key={crypto.value}
                  className="flex items-center px-4 py-2 cursor-pointer hover:bg-gray-100"
                  onClick={() => {
                    setPayCurrency(crypto.value);  // Set selected cryptocurrency for payment
                    setIsOpen(false);
                  }}
                >
                  <p className="mr-2 w-6 h-6">{React.createElement(crypto.icon)}</p> 
                  <p className="text-gray-800">{crypto.label}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="text-red-500 text-center">{error}</div>
        )}

        {/* Submit Button */}
        <div className="text-center">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            type="submit"
            className={`w-full py-2 px-4 rounded-md text-white font-semibold transition-all duration-300 ease-in-out ${
              loading ? 'bg-gray-400' : 'bg-gradient-to-r from-blue-400 to-blue-600 hover:from-blue-500 hover:to-blue-700'
            }`}
            disabled={loading}
          >
            {loading ? 'Processing...' : 'Deposit'}
          </motion.button>
        </div>
      </form>

      {/* Display Payment URL */}
      {paymentUrl && (
        <motion.div className="mt-6 text-center">
          <p className="text-lg text-green-600 font-semibold">Payment Ready!</p>
          <a href={paymentUrl} className="text-blue-600 underline" target="_blank" rel="noopener noreferrer">Complete Payment Here</a>
        </motion.div>
      )}
    </motion.div>
  );
};
export default PaymentForm;

